import type { ApiResult } from '../types';
import type { AttorneyGeneralWizardDraft } from '../types/attorneyGeneralCaseRequest';
import type { AttorneyGeneralSaveStepsResponse, AttorneyGeneralStepsResponse } from '../types/attorneyGeneralApi';
import type { DocumentChecklistItem, LookupItem } from '../mocks/criminalMasterData';
import { COURT_TYPE_ID } from '../types/criminalApi';
import {
  type AttorneyGeneralWizardStep,
} from '../pages/wizard/attorneyGeneral/attorneyGeneralConstants';
import { DEFAULT_ATTORNEY_GENERAL_DRAFT } from '../layouts/AttorneyGeneralRequestLayout';
import { parseCourtTypesFromApi, resolveDraftCourtTypeFields } from '../utils/courtTypeLookup';
import { caseApiRequest } from './apiClient';
import {
  caseRequestStepsPath,
  type CaseRequestViewOptions,
} from './caseRequestViewOptions';
import * as criminalService from './criminalCaseRequests';
import { cachedCriminalFetch } from './criminalLookupCache';
import { getCivilLitigationNextSteps } from './civilLitigationCaseRequests';
import {
  buildSaveAttorneyGeneralPayload,
  caseRequestIdFromResponse,
  mapAttorneyGeneralStepsToDraft,
  referenceNoFromResponse,
  resolveAttorneyGeneralDraftResumeStep,
} from './attorneyGeneralCaseMapper';

export const getFileCategories = criminalService.getFileCategories;
export const getSubFileCategories = criminalService.getSubFileCategories;
export const getSubCategoryApplicableAreas = criminalService.getSubCategoryApplicableAreas;
export const getProvinces = criminalService.getProvinces;
export const getDistricts = criminalService.getDistricts;
export const getPoliceStations = criminalService.getPoliceStations;
export async function getMagistrateCourts(
  districtId: string,
  provinceId?: string,
): Promise<ApiResult<LookupItem[]>> {
  return criminalService.getCourts(COURT_TYPE_ID.MAGISTRATE_COURT, districtId, provinceId);
}
export async function getHighCourts(
  districtId: string,
  provinceId?: string,
): Promise<ApiResult<LookupItem[]>> {
  return criminalService.getCourts(COURT_TYPE_ID.HIGH_COURT, districtId, provinceId);
}
export async function getCountries(): Promise<ApiResult<LookupItem[]>> {
  return cachedCriminalFetch('countries', async () => {
    const res = await caseApiRequest<unknown>('/countries');
    if (!res.ok) return { success: false, message: res.message };
    if (!Array.isArray(res.data)) return { success: true, data: [] };
    const rows: LookupItem[] = [];
    for (const entry of res.data) {
      if (!entry || typeof entry !== 'object') continue;
      const r = entry as Record<string, unknown>;
      const name = typeof r.name === 'string' ? r.name.trim() : '';
      const id = typeof r.id === 'string' ? r.id.trim() : '';
      if (name && id) rows.push({ id, name });
    }
    return { success: true, data: rows };
  });
}
export const getUrgencyLevels = criminalService.getUrgencyLevels;
export const getDeliveryMethods = criminalService.getDeliveryMethods;
export const getGenders = criminalService.getGenders;
export const getRespondentTypes = criminalService.getRespondentTypes;
export const getSuspectStatuses = criminalService.getSuspectStatuses;
export const getPreferredLanguages = criminalService.getLanguages;
export const getDocumentChecklist = criminalService.getDocumentChecklist;
export const uploadCaseFile = criminalService.uploadCaseFile;
export const downloadCaseFile = criminalService.downloadCaseFile;
export {
  getCommonCoverNote as getAttorneyGeneralCoverNote,
  downloadCommonCoverNotePdf as downloadAttorneyGeneralCoverNotePdf,
} from './coverNoteRequests';

export const getAttorneyGeneralCourts = criminalService.getCourts;

/** CMS-MNA-1 — GET /v1/case-requests/nationalities */
export const getNationalities = criminalService.getNationalities;

export const getCustodyStatuses = criminalService.getCurrentCustodyStatuses;

/** CMS-MMOA-1 — GET /v1/case-requests/methods-of-access */
export const getPreferredMethodOfAccess = criminalService.getMethodsOfAccess;

/** CMS-MCLNS-1 — next steps (shared civil litigation lookup) */
export const getAttorneyGeneralNextSteps = getCivilLitigationNextSteps;

export async function getAttorneyGeneralCourtTypes(): Promise<ApiResult<LookupItem[]>> {
  return cachedCriminalFetch('attorney-general-court-types', async () => {
    const res = await caseApiRequest<unknown>('/court-types');
    if (!res.ok) return { success: false, message: res.message };
    const rows = parseCourtTypesFromApi(res.data);
    return { success: true, data: rows };
  });
}

async function normalizeAttorneyGeneralCourtTypeDraft(
  draft: AttorneyGeneralWizardDraft,
): Promise<AttorneyGeneralWizardDraft> {
  if (!draft.courtTypeId.trim()) return draft;
  const courtTypesResult = await getAttorneyGeneralCourtTypes();
  const courtTypes = courtTypesResult.success ? courtTypesResult.data : [];
  const resolved = resolveDraftCourtTypeFields(courtTypes, {
    courtTypeId: draft.courtTypeId,
    courtTypeName: draft.courtTypeName,
  });
  if (
    resolved.courtTypeId === draft.courtTypeId &&
    resolved.courtTypeName === draft.courtTypeName
  ) {
    return draft;
  }
  return { ...draft, ...resolved };
}

export type SaveAttorneyGeneralResult = {
  requestId: string;
  fileReferenceNumber?: string;
};

/** CMS-AGCR-1 — save/update attorney general steps 1–12 */
export async function saveAttorneyGeneralRequest(
  draft: AttorneyGeneralWizardDraft,
  options: {
    isDraft: boolean;
    currentStep?: AttorneyGeneralWizardStep;
    allSteps?: boolean;
    confirmedCorrect?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): Promise<ApiResult<SaveAttorneyGeneralResult>> {
  const normalizedDraft = await normalizeAttorneyGeneralCourtTypeDraft(draft);
  const body = buildSaveAttorneyGeneralPayload(normalizedDraft, {
    isDraft: options.isDraft,
    currentStep: options.currentStep,
    allSteps: options.allSteps,
    confirmedCorrect: options.confirmedCorrect,
    isUpdateAlreadySubmitted: options.isUpdateAlreadySubmitted,
  });

  const res = await caseApiRequest<AttorneyGeneralSaveStepsResponse>('/attorney-general/steps/save', {
    method: 'POST',
    body,
  });

  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || draft.draftRequestId;

  return {
    success: true,
    data: {
      requestId,
      fileReferenceNumber: referenceNoFromResponse(res.data as AttorneyGeneralStepsResponse),
    },
  };
}

export type AttorneyGeneralRequestViewData = {
  draft: AttorneyGeneralWizardDraft;
  statusName: string;
  submittedAt: string;
  completionRemarks?: string;
  step1?: AttorneyGeneralStepsResponse['step_1'];
  step2?: AttorneyGeneralStepsResponse['step_2'];
  step3?: AttorneyGeneralStepsResponse['step_3'];
  step4?: AttorneyGeneralStepsResponse['step_4'];
  step5?: AttorneyGeneralStepsResponse['step_5'];
  step6?: AttorneyGeneralStepsResponse['step_6'];
  step7?: AttorneyGeneralStepsResponse['step_7'];
  step8?: AttorneyGeneralStepsResponse['step_8'];
  step9?: AttorneyGeneralStepsResponse['step_9'];
  step10?: AttorneyGeneralStepsResponse['step_10'];
  step11?: AttorneyGeneralStepsResponse['step_11'];
  step12?: AttorneyGeneralStepsResponse['step_12'];
};

/** CMS-AGCR-2 — full GET payload (wizard resume + view) */
export async function getAttorneyGeneralRequestViewData(
  caseRequestId: string,
  options?: CaseRequestViewOptions,
): Promise<ApiResult<AttorneyGeneralRequestViewData | null>> {
  if (!caseRequestId.trim()) return { success: true, data: null };

  const res = await caseApiRequest<AttorneyGeneralStepsResponse>(
    caseRequestStepsPath(`/attorney-general/steps/${encodeURIComponent(caseRequestId)}`, options),
    {
      ...(options?.signal ? { signal: options.signal } : {}),
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const draft = await normalizeAttorneyGeneralCourtTypeDraft(
    mapAttorneyGeneralStepsToDraft(res.data, {
      ...DEFAULT_ATTORNEY_GENERAL_DRAFT,
      draftRequestId: caseRequestIdFromResponse(res.data) || caseRequestId,
    }),
  );

  const statusName = (res.data.status_name ?? res.data.status_code ?? '').trim();
  const submittedAt = (res.data.created_at ?? res.data.updated_at ?? res.data.updatedAt ?? '').trim();
  const raw = res.data as { completion_remarks?: string };

  return {
    success: true,
    data: {
      draft,
      statusName,
      submittedAt,
      completionRemarks: typeof raw.completion_remarks === 'string' ? raw.completion_remarks : undefined,
      step1: res.data.step_1,
      step2: res.data.step_2,
      step3: res.data.step_3,
      step4: res.data.step_4,
      step5: res.data.step_5,
      step6: res.data.step_6,
      step7: res.data.step_7,
      step8: res.data.step_8,
      step9: res.data.step_9,
      step10: res.data.step_10,
      step11: res.data.step_11,
      step12: res.data.step_12,
    },
  };
}

export async function getAttorneyGeneralRequestDetails(
  caseRequestId: string,
): Promise<ApiResult<AttorneyGeneralWizardDraft | null>> {
  const view = await getAttorneyGeneralRequestViewData(caseRequestId);
  if (!view.success) return { success: false, message: view.message };
  return { success: true, data: view.data?.draft ?? null };
}

export async function getAttorneyGeneralDraftResumeStep(
  caseRequestId: string,
): Promise<ApiResult<{ step: AttorneyGeneralWizardStep; requestId: string }>> {
  if (!caseRequestId.trim()) return { success: false, message: 'Case request id is missing.' };

  const res = await caseApiRequest<AttorneyGeneralStepsResponse>(
    `/attorney-general/steps/${encodeURIComponent(caseRequestId)}`,
  );
  if (!res.ok) return { success: false, message: res.message };

  const requestId = caseRequestIdFromResponse(res.data) || caseRequestId;
  return {
    success: true,
    data: { step: resolveAttorneyGeneralDraftResumeStep(res.data), requestId },
  };
}

export async function submitAttorneyGeneralRequest(
  draft: AttorneyGeneralWizardDraft,
): Promise<ApiResult<SaveAttorneyGeneralResult>> {
  return saveAttorneyGeneralRequest(draft, {
    isDraft: false,
    allSteps: true,
    confirmedCorrect: draft.confirmCorrect,
  });
}

export async function getDocumentChecklistForCategory(
  fileCategoryId: string,
  subFileCategoryId: string,
): Promise<ApiResult<DocumentChecklistItem[]>> {
  if (!fileCategoryId) return { success: true, data: [] };
  return getDocumentChecklist(fileCategoryId, subFileCategoryId);
}
